import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * #362 3D 物理弹球 (3D Physics Bouncing Balls)
 * 
 * 来源：2026 创意编码趋势 - 3D 微交互
 * 类型：3D 物理模拟 + 粒子系统
 * 技术要点：
 * - 真实 3D 物理模拟（重力、弹性）
 * - 球坐标生成（20 个 3D 球体）
 * - 透视投影算法
 * - 深度排序（画家算法）
 * - 边界碰撞检测
 * - HSL 颜色渐变
 * - 3D 旋转效果
 * 
 * 适用场景：物理演示、游戏效果、教育视频、互动体验
 * 实现难度：中高
 */

interface Ball3D {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  radius: number;
  hue: number;
  trail: Array<{ x: number; y: number; z: number }>;
}

const Ball3DComponent: React.FC<{
  ball: Ball3D;
  frame: number;
  fps: number;
  perspective: number;
}> = ({ ball, frame, fps, perspective }) => {
  // 计算球体位置（3D 到 2D 投影）
  const scale = perspective / (perspective + ball.z * 100);
  const screenX = 960 + ball.x * scale;
  const screenY = 540 + ball.y * scale;
  const size = ball.radius * scale * 2;

  // 深度颜色调整
  const depthColor = interpolate(ball.z, [-5, 5], [0.4, 1]);
  const opacity = interpolate(ball.z, [-5, 5], [0.5, 1]);

  // 球体渐变（模拟 3D 光照）
  const gradient = `radial-gradient(circle at 30% 30%, 
    hsl(${ball.hue}, 80%, ${70 * depthColor}%), 
    hsl(${ball.hue}, 80%, ${40 * depthColor}%), 
    hsl(${ball.hue}, 90%, ${20 * depthColor}%)
  )`;

  // 阴影（基于高度）
  const shadowY = 540 - ball.y * 0.2;
  const shadowScale = interpolate(ball.y, [-200, 200], [1.5, 0.8]);
  const shadowOpacity = interpolate(ball.y, [-200, 200], [0.1, 0.4]);

  return (
    <>
      {/* 轨迹线 */}
      {ball.trail.length > 1 && (
        <svg
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            zIndex: 5,
          }}
        >
          <path
            d={`M ${960 + ball.trail[0].x * (perspective / (perspective + ball.trail[0].z * 100))} 
                ${540 + ball.trail[0].y * (perspective / (perspective + ball.trail[0].z * 100))}
                ${ball.trail
                  .slice(1)
                  .map(
                    (p) =>
                      `L ${960 + p.x * (perspective / (perspective + p.z * 100))} 
                       ${540 + p.y * (perspective / (perspective + p.z * 100))}`
                  )
                  .join(' ')}`}
            stroke={`hsla(${ball.hue}, 80%, 60%, 0.3)`}
            strokeWidth={2 * scale}
            fill="none"
            strokeLinecap="round"
          />
        </svg>
      )}

      {/* 阴影 */}
      <div
        style={{
          position: 'absolute',
          left: screenX - size * shadowScale,
          top: shadowY - size * 0.1,
          width: size * 2 * shadowScale,
          height: size * 0.2 * shadowScale,
          background: `rgba(0, 0, 0, ${shadowOpacity})`,
          borderRadius: '50%',
          filter: 'blur(10px)',
          zIndex: 1,
        }}
      />

      {/* 球体 */}
      <div
        style={{
          position: 'absolute',
          left: screenX - size,
          top: screenY - size,
          width: size * 2,
          height: size * 2,
          background: gradient,
          borderRadius: '50%',
          boxShadow: `
            inset -${size * 0.2}px -${size * 0.2}px ${size * 0.4}px rgba(0, 0, 0, 0.4),
            0 ${size * 0.1}px ${size * 0.3}px rgba(0, 0, 0, 0.3)
          `,
          opacity,
          zIndex: 10,
        }}
      />
    </>
  );
};

export const Physics3DBouncingBalls: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const perspective = 800;

  // 物理模拟参数
  const gravity = 0.5;
  const damping = 0.98;
  const bounce = 0.85;
  const bounds = { x: 600, y: 400, z: 5 };

  // 初始化球体
  const balls = React.useMemo<Array<Ball3D>>(() => {
    return Array.from({ length: 20 }, (_, i) => ({
      x: (Math.random() - 0.5) * bounds.x * 2,
      y: (Math.random() - 0.5) * bounds.y * 2 - 200,
      z: (Math.random() - 0.5) * bounds.z * 2,
      vx: (Math.random() - 0.5) * 8,
      vy: (Math.random() - 0.5) * 5,
      vz: (Math.random() - 0.5) * 2,
      radius: Math.random() * 30 + 20,
      hue: (i * 18 + frame) % 360,
      trail: [],
    }));
  }, []);

  // 物理模拟（每帧更新）
  const simulatedBalls = balls.map((ball) => {
    const newBall = { ...ball };

    // 应用重力
    newBall.vy += gravity;

    // 应用阻尼
    newBall.vx *= damping;
    newBall.vy *= damping;
    newBall.vz *= damping;

    // 更新位置
    newBall.x += newBall.vx;
    newBall.y += newBall.vy;
    newBall.z += newBall.vz;

    // 边界碰撞检测（X 轴）
    if (newBall.x > bounds.x || newBall.x < -bounds.x) {
      newBall.vx *= -bounce;
      newBall.x = Math.max(-bounds.x, Math.min(bounds.x, newBall.x));
    }

    // 边界碰撞检测（Y 轴）
    if (newBall.y > bounds.y) {
      newBall.vy *= -bounce;
      newBall.y = bounds.y;
      // 地面摩擦
      newBall.vx *= 0.95;
    }
    if (newBall.y < -bounds.y) {
      newBall.vy *= -bounce;
      newBall.y = -bounds.y;
    }

    // 边界碰撞检测（Z 轴）
    if (newBall.z > bounds.z || newBall.z < -bounds.z) {
      newBall.vz *= -bounce;
      newBall.z = Math.max(-bounds.z, Math.min(bounds.z, newBall.z));
    }

    // 更新轨迹
    newBall.trail = [...newBall.trail, { x: newBall.x, y: newBall.y, z: newBall.z }].slice(-10);

    // 更新颜色（动态变化）
    newBall.hue = (newBall.hue + 0.5) % 360;

    return newBall;
  });

  // 深度排序（画家算法）
  const sortedBalls = [...simulatedBalls].sort((a, b) => a.z - b.z);

  // 中心文字动画
  const textProgress = spring({
    frame: frame - 60,
    fps,
    config: {
      damping: 15,
      stiffness: 100,
    },
  });

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      {/* 网格地面（透视） */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          opacity: 0.3,
        }}
      >
        {Array.from({ length: 20 }).map((_, i) => {
          const y = 540 + i * 30;
          const width = interpolate(i, [0, 19], [200, 1800]);

          return (
            <div
              key={`grid-${i}`}
              style={{
                position: 'absolute',
                left: '50%',
                top: y,
                width,
                height: 1,
                background: `rgba(100, 200, 255, ${0.3 - i * 0.015})`,
                transform: `translateX(-50%)`,
              }}
            />
          );
        })}
      </div>

      {/* 渲染所有球体（已排序） */}
      {sortedBalls.map((ball, i) => (
        <Ball3DComponent
          key={`ball-${i}`}
          ball={ball}
          frame={frame}
          fps={fps}
          perspective={perspective}
        />
      ))}

      {/* 中心文字 */}
      <div
        style={{
          position: 'absolute',
          transform: `scale(${textProgress})`,
          opacity: textProgress,
          textAlign: 'center',
          zIndex: 100,
        }}
      >
        <div
          style={{
            fontSize: 100,
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #4facfe, #00f2fe)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 0 40px rgba(79, 172, 254, 0.5)',
            marginBottom: 20,
          }}
        >
          3D 物理模拟
        </div>
        <div
          style={{
            fontSize: 40,
            color: '#4facfe',
            opacity: 0.8,
          }}
        >
          Real-time Physics Simulation ⚽
        </div>
      </div>

      {/* 数据面板 */}
      <div
        style={{
          position: 'absolute',
          top: 40,
          right: 40,
          padding: 20,
          background: 'rgba(0, 0, 0, 0.5)',
          borderRadius: 10,
          color: '#4facfe',
          fontSize: 18,
          fontFamily: 'monospace',
          backdropFilter: 'blur(10px)',
        }}
      >
        <div>Balls: 20</div>
        <div>Gravity: {gravity.toFixed(2)}</div>
        <div>Bounce: {(bounce * 100).toFixed(0)}%</div>
        <div>Frame: {frame}</div>
      </div>
    </AbsoluteFill>
  );
};
